"use client";

import { useCurrentUser } from "@zapier/client";
import { useTranslations } from "@zapier/i18n";
import { SparkSurfaceLoader } from "@zapier/spark-ui";
import { useNotification } from "@zapier/utils";
import { useCallback } from "react";

import styles from "./AssetsSparkSurface.module.css";

interface AssetsSparkSurfaceProps {
  surfaceId: string | undefined;
}

/**
 * Renders the shared Asset Management Spark surface below the page header.
 *
 * Each page passes its own `surfaceId` (typically from a page-specific
 * NEXT_PUBLIC_SPARK_SURFACE_ID_* env var). If the surface id or the Zapier
 * origin env var is missing, nothing is rendered so we don't break pages
 * in environments where Spark isn't configured
 *
 * `isLoggedIn` gates whether Spark mounts the bookmark provider that powers
 * Save for Later, so we derive it here from the current session rather than
 * relying on every consumer to pass it (see REC-2997).
 *
 * `storageScopeId` turns on the loader's localStorage persistence. It is scoped
 * by user and account so a cache is never shared across accounts; the loader
 * namespaces it by `surfaceId` on its own.
 */

// Tracks which surfaceIds have already shown a fetch-error notification this
// page load so burst retries don't produce duplicate toasts, while still
// allowing a different surface that fails later to show its own error.
const shownSurfaceErrors = new Set<string>();

export function AssetsSparkSurface({ surfaceId }: AssetsSparkSurfaceProps) {
  const baseUrl = process.env.NEXT_PUBLIC_ZAPIER_ORIGIN;
  const { user } = useCurrentUser();
  const { createNotification } = useNotification();
  const t = useTranslations("assets-spark-surface");

  const handleFetchError = useCallback(() => {
    if (!surfaceId || shownSurfaceErrors.has(surfaceId)) return;
    shownSurfaceErrors.add(surfaceId);
    createNotification({
      type: "error",
      message: t("notifications.fetch-error"),
    });
  }, [createNotification, t, surfaceId]);

  if (!baseUrl || !surfaceId) {
    return;
  }

  const storageScopeId =
    user?.isLoggedIn && typeof user.currentAccountId === "number"
      ? `assetmanagement:${user.id}:${user.currentAccountId}`
      : undefined;

  return (
    <div className={styles.wrapper}>
      <SparkSurfaceLoader
        baseUrl={baseUrl}
        surfaceId={surfaceId}
        isLoggedIn={!!user?.isLoggedIn}
        storageScopeId={storageScopeId}
        prefetch
        onFetchError={handleFetchError}
      />
    </div>
  );
}
